import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchAllMaps } from './mapQueries';
import { updateMap, deleteMap, createMap } from './mapCrud';
import { MapData } from './types/MapData';
import { CalculationInput, CalculationResult } from '@/types/Calculator';

import { createMapFromCalculation } from '@/features/maps/utils/mapMapper';

/**
 * Synchronously retrieves and parses cached maps array from localStorage.
 */
export function getCachedSavedMaps(userId?: string): MapData[] | null {
    if (typeof window === 'undefined' || !userId) return null;
    try {
        const item = localStorage.getItem(`spirit_saved_maps_${userId}`);
        if (item) {
            const parsed = JSON.parse(item);
            if (Array.isArray(parsed)) {
                return parsed as MapData[];
            }
        }
    } catch (e) {
        console.warn('[useMaps] Failed to parse cached saved maps:', e);
    }
    return null;
}

/**
 * Persists cached saved maps array in localStorage.
 */
export function setCachedSavedMaps(userId?: string, maps?: MapData[] | null) {
    if (typeof window === 'undefined' || !userId) return;
    try {
        if (maps) {
            localStorage.setItem(`spirit_saved_maps_${userId}`, JSON.stringify(maps));
        } else {
            localStorage.removeItem(`spirit_saved_maps_${userId}`);
        }
    } catch (e) {
        console.warn('[useMaps] Failed to persist cached saved maps:', e);
    }
}

export const useMaps = (userId: string | undefined) => {
    const queryClient = useQueryClient();

    const mapsQuery = useQuery({
        queryKey: ['maps', userId],
        queryFn: async () => {
            const data = await fetchAllMaps(userId!);
            setCachedSavedMaps(userId, data);
            return data;
        },
        enabled: !!userId,
        initialData: () => getCachedSavedMaps(userId) || undefined,
        staleTime: 1000 * 60 * 5, // 5 minutes — keeps data fresh and avoids unneeded refetches
        gcTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
        refetchOnWindowFocus: false, // Prevent noisy refetches on tab switch
    });

    const createMutation = useMutation({
        mutationFn: ({ input, result, notes, labels, bypassDuplicateCheck, gender, soulRoot }: {
            input: CalculationInput;
            result: CalculationResult;
            notes: string | null;
            labels: string[];
            bypassDuplicateCheck?: boolean;
            gender?: string | null;
            soulRoot?: string | null;
        }) => createMap(userId!, input, result, notes, labels, undefined, bypassDuplicateCheck, gender, soulRoot),
        // When mutate is called:
        onMutate: async (newMapParams) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({ queryKey: ['maps', userId] });

            // Snapshot the previous value
            const previousMaps = queryClient.getQueryData<MapData[]>(['maps', userId]);

            // Optimistically update to the new value
            if (previousMaps) {
                const tempId = `temp-${Date.now()}`;
                const optimisticMap = createMapFromCalculation({
                    userId: userId!,
                    input: newMapParams.input,
                    result: newMapParams.result,
                    notes: newMapParams.notes,
                    labels: newMapParams.labels,
                    id: tempId
                });

                const updated = [optimisticMap, ...(previousMaps || [])];
                queryClient.setQueryData<MapData[]>(['maps', userId], updated);
                setCachedSavedMaps(userId, updated);
            }

            // Return a context object with the snapshotted value
            return { previousMaps };
        },
        // If the mutation fails, use the context returned from onMutate to roll back
        onError: (_err, _newMap, context) => {
            if (context?.previousMaps) {
                queryClient.setQueryData(['maps', userId], context.previousMaps);
                setCachedSavedMaps(userId, context.previousMaps);
            }
        },
        onSuccess: (data) => {
            if (data?.id) {
                const currentMaps = queryClient.getQueryData<MapData[]>(['maps', userId]);
                if (currentMaps) {
                    const replaced = currentMaps.map(m => m.id.startsWith('temp-') ? { ...m, id: data.id } : m);
                    queryClient.setQueryData<MapData[]>(['maps', userId], replaced);
                    setCachedSavedMaps(userId, replaced);
                }
            }
        },
        // Always refetch after error or success:
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['maps', userId] });
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, updates }: { id: string; updates: Partial<MapData> }) =>
            updateMap(id, updates),
        // When mutate is called:
        onMutate: async ({ id, updates }) => {
            // Cancel any outgoing refetches
            // (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({ queryKey: ['maps', userId] });
            await queryClient.cancelQueries({ queryKey: ['myMap', userId] });

            // Snapshot the previous values
            const previousMaps = queryClient.getQueryData<MapData[]>(['maps', userId]);
            const previousMyMap = queryClient.getQueryData<MapData | null>(['myMap', userId]);

            // Optimistically update maps list
            if (previousMaps) {
                const updated = previousMaps.map((map) =>
                    map.id === id ? { ...map, ...updates } : map
                );
                queryClient.setQueryData<MapData[]>(['maps', userId], updated);
                setCachedSavedMaps(userId, updated);
            }

            // Optimistically update myMap
            if (previousMyMap && previousMyMap.id === id) {
                if (updates.labels && !updates.labels.includes('אני')) {
                    // 'אני' label removed
                    queryClient.setQueryData(['myMap', userId], null);
                } else {
                    // Labels changed or other fields updated on myMap
                    queryClient.setQueryData(['myMap', userId], { ...previousMyMap, ...updates });
                }
            } else if (updates.labels && updates.labels.includes('אני')) {
                // If a different map is labeled as 'אני', it becomes the new myMap
                const targetMap = previousMaps?.find(m => m.id === id);
                if (targetMap) {
                    queryClient.setQueryData(['myMap', userId], { ...targetMap, ...updates });
                }
            }

            // Return a context object with the snapshotted values
            return { previousMaps, previousMyMap };
        },
        // If the mutation fails,
        // use the context returned from onMutate to roll back
        onError: (_err, _newMap, context) => {
            if (context?.previousMaps) {
                queryClient.setQueryData(['maps', userId], context.previousMaps);
                setCachedSavedMaps(userId, context.previousMaps);
            }
            if (context && 'previousMyMap' in context) {
                queryClient.setQueryData(['myMap', userId], context.previousMyMap);
            }
        },
        // Always refetch after error or success:
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['maps', userId] });
            queryClient.invalidateQueries({ queryKey: ['myMap', userId] });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => deleteMap(id),
        onMutate: async (id) => {
            await queryClient.cancelQueries({ queryKey: ['maps', userId] });
            await queryClient.cancelQueries({ queryKey: ['myMap', userId] });

            const previousMaps = queryClient.getQueryData<MapData[]>(['maps', userId]);
            const previousMyMap = queryClient.getQueryData<MapData | null>(['myMap', userId]);

            if (previousMaps) {
                const updated = previousMaps.filter((map) => map.id !== id);
                queryClient.setQueryData<MapData[]>(['maps', userId], updated);
                setCachedSavedMaps(userId, updated);
            }

            if (previousMyMap && previousMyMap.id === id) {
                queryClient.setQueryData(['myMap', userId], null);
            }

            return { previousMaps, previousMyMap };
        },
        onError: (_err, _id, context) => {
            if (context?.previousMaps) {
                queryClient.setQueryData(['maps', userId], context.previousMaps);
                setCachedSavedMaps(userId, context.previousMaps);
            }
            if (context && 'previousMyMap' in context) {
                queryClient.setQueryData(['myMap', userId], context.previousMyMap);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['maps', userId] });
            queryClient.invalidateQueries({ queryKey: ['myMap', userId] });
        },
    });

    return {
        maps: mapsQuery.data || [],
        isLoading: mapsQuery.isLoading,
        error: mapsQuery.error,
        createMap: createMutation.mutateAsync,
        updateMap: updateMutation.mutateAsync,
        deleteMap: deleteMutation.mutateAsync,
    };
};
